import React, { useEffect, useMemo } from 'react';
import ogImage from 'src/images/og_image.png';
import favicon from 'src/images/favicon_32.png';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';
import useFeatureFlag from 'src/hooks/useFeatureFlag';

interface HeadProps {
  canonicalTagSlug?: string;
  location?: { pathname: string };
  hasEmbedInstagramPost?: boolean;
  embedHubSpotForms?: boolean;
  noIndex?: boolean;

  helmet?: {
    title: string;
    description?: string;
    previewImageURL?: string;
    author?: string;
    estimatedReadingTime?: string;
  };
}

export const HeadComponent = ({
  canonicalTagSlug,
  location,
  hasEmbedInstagramPost,
  noIndex,
  helmet,
  embedHubSpotForms = true,
}: HeadProps): JSX.Element => {
  const isDevEnv = process.env.GATSBY_SITE_ENV === 'development';
  const isScriptless = process.env.GATSBY_SITE_ENV === 'scriptless';
  // Development site ENV or specific ROBOTS_INDEX should block site from indexing
  const isNotCrawlable = noIndex || isDevEnv || process.env.GATSBY_ROBOTS_INDEX === 'noindex';
  const disableAnalytics = useFeatureFlag('disable_analytics');
  const host = process.env.URL || 'https://www.mightynetworks.com';
  // Remove trailing slash for homepage only
  const pathname = location?.pathname === '/' ? '' : location?.pathname;
  const articleMetaTags = useMemo<{ name: string; content: string }[]>(() => {
    const tags = [];
    let tagIndex = 1;

    if (helmet?.author) {
      tags.push({ name: `twitter:label${tagIndex}`, content: 'Written by' });
      tags.push({ name: `twitter:data${tagIndex}`, content: helmet.author });
      tagIndex += 1;
    }

    if (helmet?.estimatedReadingTime) {
      tags.push({ name: `twitter:label${tagIndex}`, content: 'Est. reading time' });
      tags.push({ name: `twitter:data${tagIndex}`, content: helmet.estimatedReadingTime });
    }

    if (helmet?.previewImageURL) {
      const imageUrl = helmet?.previewImageURL.startsWith('http')
        ? helmet?.previewImageURL
        : `https:${helmet?.previewImageURL}`;
      tags.push({ name: 'twitter:image', content: imageUrl });
    }

    return tags;
  }, [helmet]);

  useEffect(() => {
    if (isScriptless) {
      return;
    }

    windowIsLoaded().then(() => {
      const scriptsArray: { url: string; dataDomainScript?: string; ts?: number }[] = [
        { url: 'https://cdn.cookielaw.org/consent/d21ca0aa-c1d7-429a-9361-28df92448326/OtAutoBlock.js' },
        {
          url: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
          dataDomainScript: 'd21ca0aa-c1d7-429a-9361-28df92448326',
        },
        { url: '/scripts/bing.init.js', ts: 2000 },
        { url: '/scripts/googletagmanager.init.js', ts: 2000 },
        { url: '/scripts/hotjar.init.js', ts: 2000 },
        // >> ts deferring is necessary to allow outfunnel script to work correctly, less TS will crash due to iFrame issue
        { url: '/scripts/outfunnel.c.min.js?v=' + new Date().toISOString().substring(0, 10), ts: 5000 },
      ];

      if (hasEmbedInstagramPost) {
        scriptsArray.push({ url: 'https://www.instagram.com/embed.js' });
      }

      scriptsArray.forEach(({ url: scriptLink, dataDomainScript, ts }) => {
        if (!document.querySelector(`script[src="${scriptLink}"]`) && !isScriptless) {
          const script = document.createElement('script');
          script.src = scriptLink;
          if (dataDomainScript) {
            script.setAttribute('data-domain-script', dataDomainScript);
          }
          if (ts) {
            setTimeout(() => {
              if (!document.querySelector(`script[src="${scriptLink}"]`)) {
                // Async race condition guard
                document.body.appendChild(script);
              }
            }, ts);
          } else {
            document.body.appendChild(script);
          }
          return () => {
            document.body.removeChild(script);
          };
        }

        return;
      });
    });
  }, []);

  return (
    <>
      <title>{helmet?.title || 'Mighty Networks'}</title>
      <meta charSet='utf-8' />
      <meta
        name='description'
        content={helmet?.description || ''}
      />

      <meta
        property='og:locale'
        content='en_US'
      />
      <meta
        property='og:description'
        content={helmet?.description || ''}
      />
      <meta
        property='og:type'
        content='website'
      />
      <meta
        property='og:title'
        content={helmet?.title || 'Mighty Networks'}
      />
      <meta
        property='og:url'
        content={`${host}${pathname}`}
      />
      <meta
        property='og:site_name'
        content='Mighty Networks'
      />
      <meta
        property='article:publisher'
        content='https://www.facebook.com/mightynetworks/'
      />
      <meta
        property='og:image'
        content={helmet?.previewImageURL || (isDevEnv ? ogImage : `${host}${ogImage}`)}
      />
      <meta
        property='og:image:width'
        content='1200'
      />
      <meta
        property='og:image:height'
        content='628'
      />
      <meta
        name='twitter:card'
        content='summary_large_image'
      />
      <meta
        name='twitter:site'
        content='@mightynetworks'
      />
      <meta
        name='p:domain_verify'
        content='3c82a3bc2d7827cd07ce113817f8b776'
      />

      <meta
        name='google-site-verification'
        content='sbA6mJiivGLVuHKS4y8fm_OjSNOKJHaNnvoHmv1134Y'
      />

      {isNotCrawlable ? (
        <meta
          name='robots'
          content='noindex'
        />
      ) : (
        <meta
          name='robots'
          content='index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        />
      )}
      {articleMetaTags &&
        articleMetaTags.map((tag, index) => (
          <meta
            key={index}
            name={tag.name}
            content={tag.content}
          />
        ))}
      <script type='text/javascript'>{`function OptanonWrapper() { }`}</script>
      <script>{`(window).OFID = '61098a28c77af4159f2d5f6b';`}</script>
      <script>
        {
          // UUID v4
          `var rng,crypto="undefined"!=typeof global&&(global.crypto||global.msCrypto);if(crypto&&crypto.getRandomValues){var rnds8=new Uint8Array(16);rng=function(){return crypto.getRandomValues(rnds8),rnds8}}if(!rng){var rnds=new Array(16);rng=function(){for(var r,n=0;n<16;n++)0==(3&n)&&(r=4294967296*Math.random()),rnds[n]=r>>>((3&n)<<3)&255;return rnds}}for(var byteToHex=[],i=0;i<256;++i)byteToHex[i]=(i+256).toString(16).substr(1);function bytesToUuid(r,n){var o=n||0,t=byteToHex;return t[r[o++]]+t[r[o++]]+t[r[o++]]+t[r[o++]]+"-"+t[r[o++]]+t[r[o++]]+"-"+t[r[o++]]+t[r[o++]]+"-"+t[r[o++]]+t[r[o++]]+"-"+t[r[o++]]+t[r[o++]]+t[r[o++]]+t[r[o++]]+t[r[o++]]+t[r[o++]]}function v4(r,n,o){var t=n&&o||0;"string"==typeof r&&(n="binary"==r?new Array(16):null,r=null);var a=(r=r||{}).random||(r.rng||rng)();if(a[6]=15&a[6]|64,a[8]=63&a[8]|128,n)for(var e=0;e<16;++e)n[t+e]=a[e];return n||bytesToUuid(a)}window.v4=v4;`
        }
      </script>
      <script>
        {`
          window.getCookie = function(name) {
            var match = document.cookie.match(name + '=([^;]*)');
            return match ? match[1] : undefined;
          };
          window.setCookie = function(name, value, days, domain) {
            var d = new Date();
            d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
            document.cookie = name + "=" + value + ";domain=" + domain + ";path=/;expires=" + d.toGMTString();
          };
        `}
      </script>
      {isScriptless ? null : (
        <>
          <script
            type='text/javascript'
            src='/trackjs/trackjs.js'
            defer
          ></script>
          <script
            type='text/javascript'
            src='/scripts/trackjs.init.js'
            defer
          ></script>
        </>
      )}
      {isScriptless ? null : (
        <>
          <script
            type='text/javascript'
            src='/scripts/querypropagate.init.js'
            defer
          ></script>
        </>
      )}
      {isScriptless ? null : isDevEnv ? (
        <>
          <script
            async
            src='https://www.googletagmanager.com/gtag/js?id=G-0N7XLXTNLL'
          ></script>
          {!disableAnalytics && (
            <script
              type='text/javascript'
              src='/scripts/googleanalytics.init.sandbox.js'
            ></script>
          )}
        </>
      ) : (
        <>
          <script
            async
            src='https://www.googletagmanager.com/gtag/js?id=G-7M2C06HKGP'
          ></script>
          {!disableAnalytics && (
            <script
              type='text/javascript'
              src='/scripts/googleanalytics.init.js'
            ></script>
          )}
        </>
      )}
      {isScriptless || !embedHubSpotForms ? null : (
        <>
          <script
            async
            src='https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'
          ></script>
          <script
            type='text/javascript'
            src='https://js.hsforms.net/forms/v2.js'
          ></script>
        </>
      )}
      {isScriptless ? null : (
        <script
          type='text/javascript'
          id='hs-script-loader'
          async
          defer
          src='https://js.hs-scripts.com/24271906.js'
        ></script>
      )}
      {isScriptless ? null : (
        <>
          <script
            src='https://assets.calendly.com/assets/external/forms.js'
            type='text/javascript'
            async
          />
          <script
            type='text/javascript'
            src='https://assets.calendly.com/assets/external/widget.js'
          />
        </>
      )}

      {!isScriptless && (
        <>
          <script
            type='text/javascript'
            src='https://www.wbus0a0h.com/scripts/sdk/everflow.js'
            defer
          />
          <script
            type='text/javascript'
            src='/scripts/everflow.init.js'
            defer
          />
        </>
      )}

      <link
        rel='canonical'
        href={`${host}${canonicalTagSlug ?? pathname}`}
      />
      <link
        rel='icon'
        type='image/png'
        sizes='32x32'
        href={favicon}
      />
      <link
        rel='icon'
        type='image/svg+xml'
        href='/favicon.svg'
      />
      <link
        rel='dns-prefetch'
        href='https://connect.facebook.net'
      />
      <link
        rel='preconnect'
        href='https://connect.facebook.net'
      />
      <link
        rel='dns-prefetch'
        href='https://www.facebook.com'
      />
      <link
        rel='preconnect'
        href='https://www.facebook.com'
      />
      <link
        rel='dns-prefetch'
        href='https://www.googletagmanager.com'
      />
      <link
        rel='preconnect'
        href='https://www.googletagmanager.com'
      />
    </>
  );
};
